import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Select, Button as AntButton } from "antd";
import { getGraphData } from "../../http/dashboard";
import DashboardWrapper from "../../components/DashboardWrapper";
import { Box, Typography, CircularProgress } from "@mui/material";

const { Option } = Select;

const monthMap = [
  { key: "jan", label: "Jan" },
  { key: "feb", label: "Feb" },
  { key: "mar", label: "Mar" },
  { key: "apr", label: "Apr" },
  { key: "may", label: "May" },
  { key: "jun", label: "Jun" },
  { key: "jul", label: "Jul" },
  { key: "aug", label: "Aug" },
  { key: "sep", label: "Sep" },
  { key: "oct", label: "Oct" },
  { key: "nov", label: "Nov" },
  { key: "dec", label: "Dec" },
];

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: "#fff",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "8px",
          boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
        }}
      >
        <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
        {payload.map((entry) => (
          <p
            key={entry.dataKey}
            style={{
              margin: 0,
              color: entry.color,
              fontSize: "14px",
            }}
          >
            {entry.name}: <b>{entry.value}</b>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ActiveRetentionChart = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearOptions, setYearOptions] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 6; i++) {
      years.push(currentYear - i);
    }
    setYearOptions(years);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const payload = { year: selectedYear };
        const res = await getGraphData(payload);
        const { activeUsers, retainedUsers, totalRegisteredUsers } = res.data?.data || {};

        const transformed = monthMap.map((m) => ({
          month: m.label,
          "Active Users": activeUsers?.[m.key] || 0,
          "Retention Users": retainedUsers?.[m.key] || 0,
        }));

        setChartData(transformed);
        setTotalUsers(totalRegisteredUsers)
      } catch (err) {
        console.error("Error fetching chart data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedYear]);

  // Export CSV Function
  const exportCSV = () => {
    if (!chartData.length) return;
    const headers = ["Month", "Active Users", "Retention Users"];
    const rows = chartData.map((row) => [
      row.month,
      row["Active Users"],
      row["Retention Users"],
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `active_retention_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DashboardWrapper heading="Active vs Retention Users">
      <Box
        sx={{
          padding: "30px",
          background: "#fff",
          borderRadius: 2,
          boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          position: "relative",
        }}
      >
        {/* Year Selector & Export */}
        <Box  sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Total Registered Users (All Time) : {totalUsers || 0}
            </Typography>
          </Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Select Year:
            </Typography>
            <Select
              value={selectedYear}
              style={{ width: 150 }}
              onChange={(value) => setSelectedYear(value)}
            >
              {yearOptions.map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </Box>

          <AntButton type="primary" onClick={exportCSV}>
            Export CSV
          </AntButton>
        </Box>

        {/* Loader Overlay */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Chart */}
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              label={{
                value: "Months",
                position: "insideBottom",
                offset: -5,
                style: { fontSize: 12 },
              }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{
                value: "Users",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 12 },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="top" height={36} />
            <Line
              type="monotone"
              dataKey="Active Users"
              stroke="#4f46e5"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Retention Users"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </DashboardWrapper>
  );
};

export default ActiveRetentionChart;
